import { BankQuestion } from '../bankQuestionService_moi'

export interface ScoredAnswer {
  questionId: string
  userAnswer: any
  correctAnswer: any
  isCorrect: boolean
  score: number
  maxScore: number
  feedback?: string
  timeSpent?: number
  explanation?: string
  questionType?: string
  points?: number
}

export interface QuestionAnalysis {
  questionId: string
  isCorrect: boolean
  topic?: string
  difficulty?: string
  timeSpent?: number
  explanation?: string
  hints?: string[]
  questionType?: string
  points?: number
  userAnswer?: any
  correctAnswer?: any
}

export interface ScoringResult {
  score: number
  maxScore: number
  percentage: number
  passed: boolean
  answers: ScoredAnswer[]
  feedback: string
  detailedAnalysis: QuestionAnalysis[]
  sectionScores: Record<'TN' | 'DS' | 'TL' | 'TLU', { score: number; maxScore: number; count: number }>
}

export class QuizScoringService {
  /**
   * Chấm điểm bài thi với logic cải thiện
   */
  static scoreQuiz(
    questions: BankQuestion[],
    userAnswers: Record<string, any>,
    timeSpent: number,
    passingThreshold: number = 70
  ): ScoringResult {
    console.log('🎯 Starting quiz scoring with improved logic...')

    const scoredAnswers: ScoredAnswer[] = []
    const detailedAnalysis: QuestionAnalysis[] = []

    let totalScore = 0
    let totalMaxScore = 0

    // Initialize section scores
    const sectionScores: Record<'TN' | 'DS' | 'TL' | 'TLU', { score: number; maxScore: number; count: number }> = {
      TN: { score: 0, maxScore: 0, count: 0 },
      DS: { score: 0, maxScore: 0, count: 0 },
      TL: { score: 0, maxScore: 0, count: 0 },
      TLU: { score: 0, maxScore: 0, count: 0 }
    }

    // Chấm điểm từng câu hỏi
    questions.forEach((question, index) => {
      const userAnswer = userAnswers[question.id] || null
      const questionType = question.type || (question as any).loai || 'multiple_choice'

      // Determine group
      let group: 'TN' | 'DS' | 'TL' | 'TLU' = 'TN'
      if (question.examMetadata?.group) {
        group = question.examMetadata.group as any
      } else {
        // Fallback based on type
        if (questionType === 'dung_sai' || questionType === 'true_false') group = 'DS'
        else if (questionType === 'tinh_toan' || questionType === 'short_answer' || questionType === 'tra_loi_ngan') group = 'TL'
        else group = 'TN'
      }

      // Enhanced correct answer detection with comprehensive fallback
      let correctAnswer = question.dap_an_dung

      console.log(`🔍 Question ${question.id} initial correct answer:`, {
        dap_an_dung: question.dap_an_dung,
        type: questionType,
        lua_chon: question.lua_chon,
        hasLuaChon: !!(question.lua_chon && question.lua_chon.length > 0)
      })

      // Comprehensive fallback system
      if (correctAnswer === null || correctAnswer === undefined || correctAnswer === '') {
        console.log(`⚠️ Question ${question.id}: dap_an_dung is empty, trying comprehensive fallback...`)

        // For multiple choice questions, try multiple fallback sources
        if (questionType === 'trac_nghiem_nhieu_lua_chon' || questionType === 'multiple_choice') {
          // Try 1: dap_an_dung_index
          if ((question as any).dap_an_dung_index !== undefined) {
            correctAnswer = (question as any).dap_an_dung_index
            console.log(`✅ Found correct answer from dap_an_dung_index: ${correctAnswer}`)
          }
          // Try 2: dap_an_dung_letter
          else if ((question as any).dap_an_dung_letter) {
            correctAnswer = (question as any).dap_an_dung_letter
            console.log(`✅ Found correct answer from dap_an_dung_letter: ${correctAnswer}`)
          }
          // Try 3: Look for correct answer in interactive questions (from content JSON)
          else if ((question as any).correctAnswer !== undefined) {
            correctAnswer = (question as any).correctAnswer
            console.log(`✅ Found correct answer from question.correctAnswer: ${correctAnswer}`)
          }
          // Try 4: Look for correct answer in options array
          else if (question.lua_chon && Array.isArray(question.lua_chon)) {
            // Check if any option is marked as correct
            for (let i = 0; i < question.lua_chon.length; i++) {
              const option = question.lua_chon[i]
              if (option && typeof option === 'object' && (option as any).correct === true) {
                correctAnswer = i
                console.log(`✅ Found correct answer from option[${i}].correct: ${correctAnswer}`)
                break
              }
            }
          }
          // Try 5: Default to 0 if nothing found (should rarely happen)
          if (correctAnswer === null || correctAnswer === undefined) {
            console.log(`⚠️ No correct answer found for multiple choice question ${question.id}, defaulting to 0`)
            correctAnswer = 0
          }
        }

        // For True/False questions, use statement answers
        if ((questionType === 'dung_sai' || questionType === 'true_false') && !correctAnswer) {
          // This will be handled in the scoring logic
          console.log(`ℹ️ True/False question ${question.id}: Will use statement-level answers`)
        }
      }

      const difficulty = question.muc_do || 'M1'

      console.log(`🔍 Question ${question.id} correct answer:`, {
        original: question.dap_an_dung,
        final: correctAnswer,
        type: questionType
      })

      // Sử dụng hệ thống tính điểm mới
      const { points: earnedPoints, isCorrect } = this.calculateQuestionScore(
        questionType,
        userAnswer,
        correctAnswer,
        question
      )

      // ✅ NEW: Tính maxPoints theo quy tắc mới (không dựa vào question.diem)
      let maxPoints = 1.00  // Mặc định 1 điểm (2 chữ số thập phân)
      if (questionType === 'trac_nghiem_nhieu_lua_chon' || questionType === 'multiple_choice') {
        maxPoints = 0.25  // TN: 0.25 điểm/câu (theo yêu cầu mới)
      } else if (questionType === 'tinh_toan' || questionType === 'short_answer' || questionType === 'tra_loi_ngan') {
        maxPoints = (group === 'TLU') ? 1.00 : 0.50;  // TL: 0.5, TLU: 1.0
      } else if (questionType === 'dung_sai' || questionType === 'true_false') {
        // DS: tối đa 1.0 điểm/câu (tùy số ý đúng)
        maxPoints = 1.00
      }

      // Update section scores
      if (sectionScores[group]) {
        sectionScores[group].score += earnedPoints
        sectionScores[group].maxScore += maxPoints
        sectionScores[group].count += 1
      }

      // Tính tổng điểm
      totalScore += earnedPoints
      totalMaxScore += maxPoints

      // Tạo scored answer
      const scoredAnswer: ScoredAnswer = {
        questionId: question.id,
        userAnswer,
        correctAnswer,
        isCorrect,
        score: earnedPoints,
        maxScore: maxPoints,
        timeSpent: Math.floor(timeSpent / questions.length),
        points: earnedPoints,
        explanation: question.loi_giai,
        questionType
      }

      scoredAnswers.push(scoredAnswer)

      // Tạo detailed analysis
      const analysis: QuestionAnalysis = {
        questionId: question.id,
        questionType,
        difficulty,
        userAnswer,
        correctAnswer,
        isCorrect,
        points: earnedPoints,
        timeSpent: Math.floor(timeSpent / questions.length),
        explanation: question.loi_giai,
        hints: question.goi_y
      }

      detailedAnalysis.push(analysis)

      console.log(`📝 Question ${index + 1} (${question.id}): ${isCorrect ? '✅' : '❌'} (${earnedPoints}/${maxPoints} points)`)
      console.log(`   Type: ${questionType}, User: ${JSON.stringify(userAnswer)}, Correct: ${JSON.stringify(correctAnswer)}`)
    })

    // ✅ FIX: Làm tròn tổng điểm đến 2 chữ số thập phân (không làm tròn trong quá trình tính)
    // Sử dụng toFixed(2) để đảm bảo chính xác 2 chữ số thập phân, sau đó parseFloat để loại bỏ trailing zeros
    totalScore = parseFloat(totalScore.toFixed(2))

    // Round section scores
    Object.keys(sectionScores).forEach(key => {
      const k = key as 'TN' | 'DS' | 'TL' | 'TLU'
      sectionScores[k].score = parseFloat(sectionScores[k].score.toFixed(2))
      sectionScores[k].maxScore = parseFloat(sectionScores[k].maxScore.toFixed(2))
    })

    // ✅ FIX: Tính tỷ lệ đúng với 2 chữ số thập phân
    const percentage = totalMaxScore > 0 ? parseFloat(((totalScore / totalMaxScore) * 100).toFixed(2)) : 0
    const passed = percentage >= passingThreshold

    // Tạo feedback chi tiết
    const feedback = this.generateFeedback(percentage, detailedAnalysis)

    console.log(`📊 Final Score: ${totalScore}/${totalMaxScore} (${percentage}%) - ${passed ? 'PASSED' : 'FAILED'}`)

    return {
      score: totalScore,
      maxScore: totalMaxScore,
      percentage,
      passed,
      answers: scoredAnswers,
      feedback,
      detailedAnalysis,
      sectionScores
    }
  }

  /**
   * Tính điểm cho từng câu hỏi
   */
  private static calculateQuestionScore(
    questionType: string,
    userAnswer: any,
    correctAnswer: any,
    question: BankQuestion
  ): { points: number; isCorrect: boolean } {
    // 1. Handle TLU (Essay) questions with AI grading
    if (questionType === 'tlu' || (question.examMetadata?.group === 'TLU')) {
      if (userAnswer && typeof userAnswer === 'object' && userAnswer.aiGrade) {
        const score = userAnswer.aiGrade.totalScore || 0
        const maxScore = userAnswer.aiGrade.totalMaxScore || 1
        return {
          points: score,
          isCorrect: score >= (maxScore / 2)
        }
      }
      // Fallback if no AI grade
      return { points: 0, isCorrect: false }
    }

    // 2. Handle DS (True/False) with partial credit
    if ((questionType === 'dung_sai' || questionType === 'true_false') &&
      typeof userAnswer === 'object' && userAnswer !== null && !Array.isArray(userAnswer)) {

      let correctCount = 0
      let totalCount = 0 // Should be 4 usually

      // If correctAnswer is object (multi-statement)
      if (typeof correctAnswer === 'object' && correctAnswer !== null) {
        // Count total expected answers (usually a, b, c, d)
        const keys = ['a', 'b', 'c', 'd']

        keys.forEach(key => {
          if (correctAnswer[key] !== undefined) {
            totalCount++
            // Check if user answered this key
            // CRITICAL FIX: Ensure user answer is not just an empty object or null
            if (userAnswer && userAnswer[key] !== undefined && userAnswer[key] !== null) {
              const userBool = this.normalizeBoolean(userAnswer[key])
              const correctBool = this.normalizeBoolean(correctAnswer[key])
              if (userBool === correctBool) {
                correctCount++
              }
            }
          }
        })
      }

      // Scoring scheme for DS (standard Ministry of Education format): 
      // 1 correct -> 0.1
      // 2 correct -> 0.25
      // 3 correct -> 0.5
      // 4 correct -> 1.0
      let points = 0
      if (correctCount === 1) points = 0.1
      else if (correctCount === 2) points = 0.25
      else if (correctCount === 3) points = 0.5
      else if (correctCount === 4) points = 1.0

      return { points, isCorrect: correctCount === totalCount && totalCount > 0 }
    }

    // 3. Handle other types (TN, TL) using evaluateAnswer
    const { isCorrect } = this.evaluateAnswer(questionType, userAnswer, correctAnswer, question)

    let points = 0
    if (isCorrect) {
      if (questionType === 'trac_nghiem_nhieu_lua_chon' || questionType === 'multiple_choice') {
        // If no answer, 0 points (isCorrect is already false but ensure points logic is safe)
        points = isCorrect ? 0.25 : 0
      } else if (questionType === 'tinh_toan' || questionType === 'short_answer' || questionType === 'tra_loi_ngan') {
        points = 0.5
      } else if (questionType === 'tu_luan' || questionType === 'essay' || questionType === 'TLU') {
        // For TLU, use the specific score from AI if available
        if (userAnswer && typeof userAnswer === 'object' && userAnswer.aiGrade) {
          points = userAnswer.aiGrade.totalScore;
        } else {
          points = 0; // Or manual grading default
        }
      } else {
        points = 1.0
      }
    }

    return { points, isCorrect }
  }

  /**
   * Đánh giá đáp án với logic cải thiện
   */
  private static evaluateAnswer(
    questionType: string,
    userAnswer: any,
    correctAnswer: any,
    question: BankQuestion
  ): { isCorrect: boolean; explanation?: string } {
    console.log(`🔍 Evaluating ${questionType}:`, {
      original: { user: userAnswer, correct: correctAnswer },
      userType: typeof userAnswer,
      correctType: typeof correctAnswer
    })

    let isCorrect = false
    let explanation = ''

    switch (questionType) {
      case 'trac_nghiem_nhieu_lua_chon':
      case 'multiple_choice':
        // For multiple choice, pass original data directly to text-based comparison
        isCorrect = this.compareMultipleChoice(userAnswer, correctAnswer, question)
        break

      case 'dung_sai':
      case 'true_false':
        // For True/False, still use normalized data
        const normalizedUserAnswer = this.normalizeAnswer(userAnswer)
        const normalizedCorrectAnswer = this.normalizeAnswer(correctAnswer)
        isCorrect = this.compareTrueFalse(normalizedUserAnswer, normalizedCorrectAnswer)
        break

      case 'tinh_toan':
      case 'short_answer':
        // For calculation, still use normalized data
        const normalizedUserAnswerCalc = this.normalizeAnswer(userAnswer)
        const normalizedCorrectAnswerCalc = this.normalizeAnswer(correctAnswer)
        isCorrect = this.compareCalculation(normalizedUserAnswerCalc, normalizedCorrectAnswerCalc, question)
        break

      case 'tu_luan':
      case 'essay':
      case 'TLU':
        // Handle TLU object answer
        if (userAnswer && typeof userAnswer === 'object' && userAnswer.aiGrade) {
          // If AI has graded it, use that result
          // We consider it "correct" if the score is > 0 (or some threshold), 
          // but the actual points will be assigned in calculatePoints
          isCorrect = userAnswer.aiGrade.totalScore > 0;
        } else {
          // Fallback for manual grading or legacy string answers
          // For now, assume incorrect if no AI grade, or implement basic keyword matching
          isCorrect = false;
        }
        break;

      default:
        console.warn(`⚠️ Unknown question type: ${questionType}, treating as multiple choice`)
        isCorrect = this.compareMultipleChoice(userAnswer, correctAnswer, question)
    }

    // Thêm explanation nếu có
    if (question.loi_giai) {
      explanation = question.loi_giai
    }

    return { isCorrect, explanation }
  }

  /**
   * Chuẩn hóa đáp án để so sánh
   */
  private static normalizeAnswer(answer: any): any {
    if (answer === null || answer === undefined) return null

    // Nếu là string, chuẩn hóa
    if (typeof answer === 'string') {
      let normalized = answer.trim().toLowerCase()

      // Xử lý số thập phân với dấu phẩy (chuyển thành dấu chấm)
      normalized = normalized.replace(/,/g, '.')

      // Xử lý ký hiệu khoa học (10^-3, 10⁻³, etc.)
      normalized = normalized
        .replace(/\^(-?\d+)/g, 'e$1')  // 10^-3 -> 10e-3
        .replace(/([⁻⁰¹²³⁴⁵⁶⁷⁸⁹]+)/g, (match) => {
          // Convert superscript to regular numbers
          return match
            .replace(/⁻/g, '-')
            .replace(/⁰/g, '0')
            .replace(/¹/g, '1')
            .replace(/²/g, '2')
            .replace(/³/g, '3')
            .replace(/⁴/g, '4')
            .replace(/⁵/g, '5')
            .replace(/⁶/g, '6')
            .replace(/⁷/g, '7')
            .replace(/⁸/g, '8')
            .replace(/⁹/g, '9')
        })

      // Xử lý các ký tự đặc biệt tiếng Việt
      normalized = normalized
        .replace(/[àáạảãâầấậẩẫăằắặẳẵ]/g, 'a')
        .replace(/[èéẹẻẽêềếệểễ]/g, 'e')
        .replace(/[ìíịỉĩ]/g, 'i')
        .replace(/[òóọỏõôồốộổỗơờớợởỡ]/g, 'o')
        .replace(/[ùúụủũưừứựửữ]/g, 'u')
        .replace(/[ỳýỵỷỹ]/g, 'y')
        .replace(/[đ]/g, 'd')

      // Xử lý đơn vị và khoảng trắng thừa
      normalized = normalized
        .replace(/\s+/g, ' ')
        .trim()

      // Kiểm tra xem có phải là số hay không (bao gồm cả số âm và ký hiệu khoa học)
      const numberMatch = normalized.match(/^-?\d+([.]\d+)?(\s*e\s*-?\d+)?(\s*[a-z\/\^\-\+\d\u207a-\u207f\u2070-\u2079°]*)?$/)
      if (numberMatch) {
        // For numbers with units, keep the full string but clean it up
        const cleanNumber = normalized
          .replace(/\s*e\s*/g, 'e')  // Clean up scientific notation spacing
          .replace(/\s+/g, ' ')     // Normalize spaces
          .trim()
        return cleanNumber
      }

      // Nếu không phải số, xóa ký tự đặc biệt nhưng giữ lại chữ cái và số
      normalized = normalized.replace(/[^a-z0-9\s.\-]/g, '').trim()

      return normalized
    }

    // Nếu là number, giữ nguyên
    if (typeof answer === 'number') {
      return answer
    }

    // Nếu là array, chuẩn hóa từng phần tử
    if (Array.isArray(answer)) {
      return answer.map(item => this.normalizeAnswer(item))
    }

    // Nếu là boolean, giữ nguyên
    if (typeof answer === 'boolean') {
      return answer
    }

    return answer
  }

  /**
   * So sánh câu hỏi trắc nghiệm nhiều lựa chọn - SO SÁNH TEXT TRỰC TIẾP
   */
  private static compareMultipleChoice(userAnswer: any, correctAnswer: any, question?: BankQuestion): boolean {
    console.log(`🔍 TN comparison (text-based): user=${userAnswer} (${typeof userAnswer}) vs correct=${correctAnswer} (${typeof correctAnswer})`)

    // Kiểm tra null/undefined
    if (userAnswer === null || userAnswer === undefined || correctAnswer === null || correctAnswer === undefined) {
      console.log(`🔍 TN null check: user=${userAnswer}, correct=${correctAnswer} = false`)
      return false
    }

    // Convert user answer to text of selected option
    const userOptionText = this.getOptionTextFromUserAnswer(userAnswer, question)
    if (!userOptionText) {
      console.log(`🔍 TN could not get option text from user answer: ${userAnswer}`)
      return false
    }

    // Convert correct answer to text (if it's an index)
    const correctOptionText = this.getOptionTextFromUserAnswer(correctAnswer, question) || correctAnswer

    console.log(`🔍 TN user option text: "${userOptionText}"`)
    console.log(`🔍 TN correct option text: "${correctOptionText}"`)

    // Compare text directly
    const result = this.compareTextAnswers(userOptionText, correctOptionText)
    console.log(`🔍 TN text comparison: "${userOptionText}" === "${correctOptionText}" = ${result}`)

    return result
  }

  /**
   * Get the text of the option selected by user
   */
  private static getOptionTextFromUserAnswer(userAnswer: any, question?: BankQuestion): string | null {
    if (!question || !question.lua_chon || !Array.isArray(question.lua_chon)) {
      console.log(`🔍 No lua_chon available for text extraction`)
      return null
    }

    // Convert user answer to index
    let userIndex = -1

    if (typeof userAnswer === 'number') {
      // Direct number: 0-3
      if (userAnswer >= 0 && userAnswer <= 3) {
        userIndex = userAnswer
      }
      // If it's 1-4, convert to 0-3
      else if (userAnswer >= 1 && userAnswer <= 4) {
        userIndex = userAnswer - 1
      }
    }
    else if (typeof userAnswer === 'string') {
      const trimmed = userAnswer.trim().toUpperCase()

      // Letter mapping
      if (trimmed === 'A') userIndex = 0
      else if (trimmed === 'B') userIndex = 1
      else if (trimmed === 'C') userIndex = 2
      else if (trimmed === 'D') userIndex = 3
      else {
        // Try number string
        const num = parseInt(trimmed)
        if (!isNaN(num)) {
          if (num >= 0 && num <= 3) userIndex = num
          else if (num >= 1 && num <= 4) userIndex = num - 1
        }
      }
    }

    console.log(`🔍 User answer "${userAnswer}" converted to index: ${userIndex}`)

    // Get option text by index
    if (userIndex >= 0 && userIndex < question.lua_chon.length) {
      const optionText = question.lua_chon[userIndex]
      console.log(`🔍 Option text at index ${userIndex}: "${optionText}"`)
      return optionText
    }

    return null
  }

  /**
   * Compare text answers with normalization
   */
  private static compareTextAnswers(userText: string, correctText: string): boolean {
    if (!userText || !correctText) return false

    // Normalize both texts
    const normalizedUser = userText.trim().toLowerCase().replace(/\s+/g, ' ')
    const normalizedCorrect = correctText.trim().toLowerCase().replace(/\s+/g, ' ')

    console.log(`🔍 Normalized comparison: "${normalizedUser}" vs "${normalizedCorrect}"`)

    return normalizedUser === normalizedCorrect
  }

  /**
   * So sánh câu hỏi đúng/sai (bao gồm cả multi-statement)
   */
  private static compareTrueFalse(userAnswer: any, correctAnswer: any): boolean {
    // Handle multi-statement True/False questions
    if (typeof userAnswer === 'object' && userAnswer !== null && !Array.isArray(userAnswer)) {
      // This is a multi-statement True/False question
      // userAnswer structure: { "statement_id_1": true, "statement_id_2": false, ... }

      // For now, we'll consider it correct if all answered statements are correct
      // In the future, this could be enhanced with partial scoring

      // If correctAnswer is also an object, compare statement by statement
      if (typeof correctAnswer === 'object' && correctAnswer !== null && !Array.isArray(correctAnswer)) {
        let allCorrect = true
        let hasAnswers = false

        for (const statementId in userAnswer) {
          if (userAnswer[statementId] !== null && userAnswer[statementId] !== undefined) {
            hasAnswers = true
            const userStatementAnswer = this.normalizeBoolean(userAnswer[statementId])
            const correctStatementAnswer = this.normalizeBoolean(correctAnswer[statementId])

            if (userStatementAnswer !== correctStatementAnswer) {
              allCorrect = false
              break
            }
          }
        }

        return hasAnswers && allCorrect
      }

      // If correctAnswer is not an object, we can't properly compare
      // For now, we'll use a simple heuristic: check if majority of answered statements are true
      const answeredStatements = Object.values(userAnswer).filter(answer => answer !== null && answer !== undefined)
      if (answeredStatements.length === 0) return false

      const trueCount = answeredStatements.filter(answer => this.normalizeBoolean(answer)).length
      const majorityTrue = trueCount > answeredStatements.length / 2

      return majorityTrue === this.normalizeBoolean(correctAnswer)
    }

    // Standard single True/False question
    // CRITICAL FIX: If user answer is null/undefined, it is NOT correct.
    // Do not rely on normalizeBoolean converting null to false.
    if (userAnswer === null || userAnswer === undefined || userAnswer === '') {
      return false
    }

    const user = this.normalizeBoolean(userAnswer)
    const correct = this.normalizeBoolean(correctAnswer)

    return user === correct
  }

  /**
   * So sánh câu hỏi tính toán (TL - Trả lời ngắn)
   */
  private static compareCalculation(userAnswer: any, correctAnswer: any, question: BankQuestion): boolean {
    console.log(`🔍 TL Calculation comparison:`, {
      questionId: question.id,
      userAnswer,
      correctAnswer,
      userType: typeof userAnswer,
      correctType: typeof correctAnswer
    })

    // Kiểm tra null/undefined/empty
    if (userAnswer === null || userAnswer === undefined || userAnswer === '') {
      console.log(`❌ TL: User answer is empty`)
      return false
    }

    if (correctAnswer === null || correctAnswer === undefined || correctAnswer === '') {
      console.log(`❌ TL: Correct answer is empty`)
      return false
    }

    // Chuẩn hóa đáp án người dùng (chấp nhận dấu phẩy làm phân cách thập phân)
    const normalizedUserAnswer = this.normalizeNumericAnswer(userAnswer)
    const normalizedCorrectAnswer = this.normalizeNumericAnswer(correctAnswer)

    console.log(`🔍 TL Normalized:`, {
      user: normalizedUserAnswer,
      correct: normalizedCorrectAnswer
    })

    // Nếu có đáp án thay thế, kiểm tra tất cả
    const questionAny = question as any
    if (questionAny.dap_an_thay_the && Array.isArray(questionAny.dap_an_thay_the)) {
      console.log(`🔍 TL: Checking alternative answers:`, questionAny.dap_an_thay_the)

      const isCorrectAlt = questionAny.dap_an_thay_the.some((alt: any) => {
        const normalizedAlt = this.normalizeNumericAnswer(alt)
        return this.compareNumericValues(normalizedUserAnswer, normalizedAlt)
      })

      if (isCorrectAlt) {
        console.log(`✅ TL: Match found in alternative answers`)
        return true
      }
    }

    // So sánh với đáp án chính
    const isCorrect = this.compareNumericValues(normalizedUserAnswer, normalizedCorrectAnswer)
    console.log(`${isCorrect ? '✅' : '❌'} TL: Final result = ${isCorrect}`)

    return isCorrect
  }

  /**
   * So sánh giá trị với độ chính xác
   */
  private static compareValues(userValue: any, correctValue: any): boolean {
    console.log(`🔍 Comparing values: user="${userValue}" vs correct="${correctValue}"`)

    // Handle null/undefined values
    if (userValue === null || userValue === undefined || correctValue === null || correctValue === undefined) {
      console.log(`🔍 Null/undefined comparison: false`)
      return false
    }

    // Convert to string for initial comparison
    const userStr = String(userValue).trim()
    const correctStr = String(correctValue).trim()

    // If both are empty strings after trimming, return false
    if (userStr === '' || correctStr === '') {
      console.log(`🔍 Empty string comparison: false`)
      return false
    }

    // Handle scientific notation and units
    const extractNumberFromAnswer = (str: string) => {
      // Replace Vietnamese comma with dot
      let processed = str.replace(/,/g, '.')

      // Handle scientific notation with e/E
      if (/e[+-]?\d+/i.test(processed)) {
        const match = processed.match(/-?\d+(?:\.\d+)?e[+-]?\d+/i)
        if (match) return parseFloat(match[0])
      }

      // Handle power notation (10^-3, 10⁻³)
      if (/\d+\^[+-]?\d+/.test(processed) || /\d+[⁻⁰¹²³⁴⁵⁶⁷⁸⁹]+/.test(processed)) {
        // Convert to scientific notation
        processed = processed
          .replace(/(\d+)\^([+-]?\d+)/, '$1e$2')
          .replace(/(\d+)([⁻⁰¹²³⁴⁵⁶⁷⁸⁹]+)/, (match, base, exp) => {
            const normalExp = exp
              .replace(/⁻/g, '-')
              .replace(/⁰/g, '0')
              .replace(/¹/g, '1')
              .replace(/²/g, '2')
              .replace(/³/g, '3')
              .replace(/⁴/g, '4')
              .replace(/⁵/g, '5')
              .replace(/⁶/g, '6')
              .replace(/⁷/g, '7')
              .replace(/⁸/g, '8')
              .replace(/⁹/g, '9')
            return `${base}e${normalExp}`
          })

        const match = processed.match(/-?\d+(?:\.\d+)?e[+-]?\d+/i)
        if (match) return parseFloat(match[0])
      }

      // Extract just the number part (ignore units)
      const numberMatch = processed.match(/-?\d+(?:\.\d+)?/)
      return numberMatch ? parseFloat(numberMatch[0]) : NaN
    }

    // Try to parse as numbers first (for numeric answers)
    const userNum = extractNumberFromAnswer(userStr)
    const correctNum = extractNumberFromAnswer(correctStr)

    // If both can be parsed as numbers, compare numerically with tolerance
    if (!isNaN(userNum) && !isNaN(correctNum)) {
      const tolerance = Math.max(0.001, Math.abs(correctNum) * 0.001) // Adaptive tolerance
      const result = Math.abs(userNum - correctNum) <= tolerance
      console.log(`🔍 Numeric comparison: ${userNum} vs ${correctNum} = ${result} (tolerance: ${tolerance})`)
      return result
    }

    // If not both numbers, do string comparison (case-insensitive, normalized)
    const normalizedUser = userStr.toLowerCase().replace(/\s+/g, ' ').trim()
    const normalizedCorrect = correctStr.toLowerCase().replace(/\s+/g, ' ').trim()
    const result = normalizedUser === normalizedCorrect
    console.log(`🔍 String comparison: "${normalizedUser}" vs "${normalizedCorrect}" = ${result}`)
    return result
  }

  /**
   * Chuẩn hóa boolean values
   */
  private static normalizeBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value

    if (typeof value === 'string') {
      const normalized = value.toLowerCase().trim()
      if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'đúng') return true
      if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'sai') return false
    }

    if (typeof value === 'number') {
      return value === 1
    }

    return false
  }

  /**
   * Kiểm tra xem người dùng có trả lời hợp lệ hay không
   */
  private static hasValidAnswer(userAnswer: any): boolean {
    // Null hoặc undefined = không có đáp án
    if (userAnswer === null || userAnswer === undefined) {
      return false
    }

    // String rỗng hoặc chỉ có khoảng trắng = không có đáp án
    if (typeof userAnswer === 'string' && userAnswer.trim() === '') {
      return false
    }

    // Số 0 vẫn là đáp án hợp lệ
    if (typeof userAnswer === 'number') {
      return true
    }

    // Boolean false vẫn là đáp án hợp lệ
    if (typeof userAnswer === 'boolean') {
      return true
    }

    // Object (cho câu hỏi đúng/sai nhiều phần) - kiểm tra có ít nhất 1 câu trả lời
    if (typeof userAnswer === 'object' && userAnswer !== null && !Array.isArray(userAnswer)) {
      const values = Object.values(userAnswer)
      return values.some(value => value !== null && value !== undefined)
    }

    // Array - kiểm tra có ít nhất 1 phần tử hợp lệ
    if (Array.isArray(userAnswer)) {
      return userAnswer.some(item => this.hasValidAnswer(item))
    }

    // Các trường hợp khác coi như có đáp án
    return true
  }

  /**
   * Chuyển đổi chữ cái thành index
   */
  private static letterToIndex(letter: string): number {
    const upper = letter.toUpperCase()
    if (upper === 'A') return 0
    if (upper === 'B') return 1
    if (upper === 'C') return 2
    if (upper === 'D') return 3
    return -1
  }

  /**
   * Chuyển đổi số thành chữ cái (1->A, 2->B, 3->C, 4->D)
   */
  private static numberToLetter(input: string): string | null {
    const num = parseInt(input)
    if (isNaN(num)) return null

    if (num === 1) return 'A'
    if (num === 2) return 'B'
    if (num === 3) return 'C'
    if (num === 4) return 'D'
    return null
  }

  /**
   * Chuẩn hóa đáp án số (chấp nhận dấu phẩy làm phân cách thập phân)
   */
  private static normalizeNumericAnswer(answer: any): string {
    if (answer === null || answer === undefined) return ''

    let str = String(answer).trim()

    // Chuyển dấu phẩy thành dấu chấm cho số thập phân
    str = str.replace(/,/g, '.')

    // Loại bỏ khoảng trắng thừa
    str = str.replace(/\s+/g, ' ').trim()

    // Xử lý ký hiệu khoa học
    str = str
      .replace(/\^(-?\d+)/g, 'e$1')  // 10^-3 -> 10e-3
      .replace(/\s*[xX]\s*10\s*\^?\s*(-?\d+)/g, 'e$1')  // x10^-3 -> e-3
      .replace(/\s*e\s*/g, 'e')  // Clean up e notation spacing

    console.log(`🔢 Normalized numeric: "${answer}" -> "${str}"`)
    return str
  }

  /**
   * So sánh hai giá trị số với độ chính xác
   */
  private static compareNumericValues(userValue: string, correctValue: string): boolean {
    if (!userValue || !correctValue) return false

    // Trích xuất số từ chuỗi (bỏ qua đơn vị)
    const extractNumber = (str: string): number | null => {
      // Tìm số (bao gồm cả ký hiệu khoa học)
      const numberMatch = str.match(/-?\d+(?:\.\d+)?(?:e[+-]?\d+)?/i)
      if (numberMatch) {
        const num = parseFloat(numberMatch[0])
        return isNaN(num) ? null : num
      }
      return null
    }

    const userNum = extractNumber(userValue)
    const correctNum = extractNumber(correctValue)

    console.log(`🔢 Numeric comparison:`, {
      userValue,
      correctValue,
      userNum,
      correctNum
    })

    if (userNum === null || correctNum === null) {
      // Nếu không thể parse thành số, so sánh chuỗi
      const result = userValue.toLowerCase() === correctValue.toLowerCase()
      console.log(`🔤 String comparison: "${userValue}" === "${correctValue}" = ${result}`)
      return result
    }

    // So sánh số với độ chính xác
    const tolerance = Math.max(0.001, Math.abs(correctNum) * 0.001) // 0.1% tolerance
    const difference = Math.abs(userNum - correctNum)
    const result = difference <= tolerance

    console.log(`🔢 Numeric result: |${userNum} - ${correctNum}| = ${difference} <= ${tolerance} = ${result}`)
    return result
  }

  /**
   * Tạo feedback chi tiết dựa trên kết quả
   */
  private static generateFeedback(percentage: number, analysis: QuestionAnalysis[]): string {
    const correctCount = analysis.filter(a => a.isCorrect).length
    const totalCount = analysis.length

    // Phân tích theo mức độ
    const m1Questions = analysis.filter(a => a.difficulty === 'M1')
    const m2Questions = analysis.filter(a => a.difficulty === 'M2')
    const m3Questions = analysis.filter(a => a.difficulty === 'M3')

    const m1Correct = m1Questions.filter(a => a.isCorrect).length
    const m2Correct = m2Questions.filter(a => a.isCorrect).length
    const m3Correct = m3Questions.filter(a => a.isCorrect).length

    let feedback = `📊 Kết quả: ${correctCount}/${totalCount} câu đúng (${percentage}%)\n\n`

    // Feedback theo mức độ
    if (m1Questions.length > 0) {
      feedback += `🔰 Mức cơ bản (M1): ${m1Correct}/${m1Questions.length} câu đúng\n`
    }
    if (m2Questions.length > 0) {
      feedback += `🔄 Mức trung bình (M2): ${m2Correct}/${m2Questions.length} câu đúng\n`
    }
    if (m3Questions.length > 0) {
      feedback += `🚀 Mức nâng cao (M3): ${m3Correct}/${m3Questions.length} câu đúng\n`
    }

    feedback += '\n'

    // Feedback tổng quan
    if (percentage >= 90) {
      feedback += '🎉 Xuất sắc! Bạn đã nắm vững kiến thức về chủ đề này.'
    } else if (percentage >= 80) {
      feedback += '👍 Tốt! Bạn có kiến thức khá vững về chủ đề này.'
    } else if (percentage >= 70) {
      feedback += '✅ Đạt! Bạn đã hiểu được những điểm cơ bản.'
    } else if (percentage >= 50) {
      feedback += '⚠️ Cần cải thiện! Hãy xem lại những câu sai và học thêm.'
    } else {
      feedback += '📚 Cần học lại! Hãy xem lại toàn bộ lý thuyết và làm lại bài tập.'
    }

    return feedback
  }

  /**
   * Phân tích chi tiết kết quả bài thi
   */
  static analyzeResults(analysis: QuestionAnalysis[]): {
    strengthAreas: string[]
    weakAreas: string[]
    recommendations: string[]
  } {
    const correctQuestions = analysis.filter(a => a.isCorrect)
    const incorrectQuestions = analysis.filter(a => !a.isCorrect)

    // Phân tích điểm mạnh
    const strengthAreas: string[] = []
    if (correctQuestions.length > 0) {
      const types = [...new Set(correctQuestions.map(q => q.questionType))]
      strengthAreas.push(...types.map(type => `Thành thạo loại câu hỏi: ${type}`))
    }

    // Phân tích điểm yếu
    const weakAreas: string[] = []
    if (incorrectQuestions.length > 0) {
      const types = [...new Set(incorrectQuestions.map(q => q.questionType))]
      weakAreas.push(...types.map(type => `Cần cải thiện loại câu hỏi: ${type}`))
    }

    // Đề xuất
    const recommendations: string[] = []
    if (incorrectQuestions.length > 0) {
      recommendations.push('Xem lại lời giải chi tiết cho các câu sai')
      recommendations.push('Làm thêm bài tập tương tự để củng cố kiến thức')
    }

    return { strengthAreas, weakAreas, recommendations }
  }
}
